/**
 * §6.5 — QUADRANTS AND PRIORITY.
 *
 * *"Thresholds (defaults): overlap 70%, complexity 3.0. Quick win: overlap ≥ 70 and complexity
 * < 3.0. Strategic move: ≥ 70 and ≥ 3.0. Harmonize first: < 70 and < 3.0. Defer or retain:
 * < 70 and ≥ 3.0. Priority: quadrant order as listed, then Importance Index descending. Manual
 * override requires a reason."*
 *
 * D-004 translates the second threshold and explains why it translates rather than being
 * re-chosen: *"3.0 on a 1-5 scale is the midpoint, and 60% is the same midpoint expressed in the
 * new units, so every existing quadrant boundary keeps the meaning the spec gave it. Picking a
 * rounder number such as 50% would move real cases across the Quick-win / Strategic-move line
 * for no stated reason."* The overlap threshold of 70% and the four NAMES are untouched by D-004.
 *
 * Both thresholds stay **configurable per initiative** (§6.5 *"defaults"*), stored as
 * `scoring_config.overlap_threshold_pct_tenths` and `complexity_threshold_pct_tenths` — 700 and
 * 600 tenths, which is what `0096` defaults them to, so the package default and the column
 * default are the same two numbers.
 *
 * `>=` is the spec's own operator on BOTH axes, and it is the whole of the boundary behaviour: a
 * unit at exactly 70.0000% overlap is a high-overlap unit, and one at exactly 60.0000% complexity
 * is a high-complexity unit. There is no epsilon and no tolerance — which is the point of
 * comparing integers.
 */
import { assertInteger, assertPctTenths } from './units';

export type QuadrantName = 'Quick win' | 'Strategic move' | 'Harmonize first' | 'Defer or retain';

/** §6.5's order, verbatim, and therefore the priority order. */
export const QUADRANT_ORDER: readonly QuadrantName[] = [
  'Quick win',
  'Strategic move',
  'Harmonize first',
  'Defer or retain',
];

export interface QuadrantThresholds {
  /** §6.5's 70%, in ten-thousandths: 700 000. */
  readonly overlapTenThousandths: number;
  /** §6.5's 3.0 as D-004 translates it — 60%, in ten-thousandths: 600 000. */
  readonly complexityTenThousandths: number;
}

export const DEFAULT_QUADRANT_THRESHOLDS: QuadrantThresholds = {
  overlapTenThousandths: 700_000,
  complexityTenThousandths: 600_000,
};

/**
 * Build thresholds from what `scoring_config` actually stores, which is tenths.
 *
 * The only conversion between the two units in this module, and it is a multiplication by 1000
 * with nothing to round — a threshold entered to one decimal place is exact in ten-thousandths.
 */
export function thresholdsFromTenths(
  overlapThresholdPctTenths: number,
  complexityThresholdPctTenths: number,
): QuadrantThresholds {
  assertPctTenths(overlapThresholdPctTenths, 'overlap threshold');
  assertPctTenths(complexityThresholdPctTenths, 'complexity threshold');
  return {
    overlapTenThousandths: overlapThresholdPctTenths * 1000,
    complexityTenThousandths: complexityThresholdPctTenths * 1000,
  };
}

/** §6.5's four cases, in §6.5's own words. */
export function quadrant(
  overlapTenThousandths: number,
  complexityTenThousandths: number,
  thresholds: QuadrantThresholds = DEFAULT_QUADRANT_THRESHOLDS,
): QuadrantName {
  assertInteger(overlapTenThousandths, 'quadrant: overlap');
  assertInteger(complexityTenThousandths, 'quadrant: complexity');
  const highOverlap = overlapTenThousandths >= thresholds.overlapTenThousandths;
  const highComplexity = complexityTenThousandths >= thresholds.complexityTenThousandths;
  if (highOverlap) return highComplexity ? 'Strategic move' : 'Quick win';
  return highComplexity ? 'Defer or retain' : 'Harmonize first';
}

export interface PriorityRow {
  readonly id: string;
  readonly quadrant: QuadrantName;
  readonly importanceTenThousandths: number;
}

/**
 * §6.5's priority: *"quadrant order as listed, then Importance Index descending."*
 *
 * The third key — id, ascending — is NOT a spec rule. It is there because the first two do not
 * totally order the set: two units in the same quadrant with the same Importance Index would
 * otherwise sit in whatever order `Array.prototype.sort` left them, and a report that reorders
 * itself between two runs over unchanged data is a report nobody can diff. Compared by code
 * point rather than `localeCompare` so a Node process and a browser cannot disagree — the two
 * sides are supposed to agree by construction (D-009), not because their ICU data matches.
 */
export function comparePriority(a: PriorityRow, b: PriorityRow): number {
  const qa = QUADRANT_ORDER.indexOf(a.quadrant);
  const qb = QUADRANT_ORDER.indexOf(b.quadrant);
  if (qa !== qb) return qa - qb;
  if (a.importanceTenThousandths !== b.importanceTenThousandths) {
    return b.importanceTenThousandths - a.importanceTenThousandths;
  }
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export interface PrioritizedRow<T extends PriorityRow> {
  readonly row: T;
  /** 1-based, in §6.5's order. The number a screen shows in its leftmost column. */
  readonly rank: number;
}

/**
 * The whole list in §6.5's order, with each row's rank.
 *
 * Does not mutate the input — `[...rows]` before the sort — because a scorer that reorders the
 * array its caller is still rendering from is the kind of fault that shows up as a flickering
 * table and gets blamed on React.
 */
export function prioritize<T extends PriorityRow>(rows: readonly T[]): PrioritizedRow<T>[] {
  return [...rows]
    .sort(comparePriority)
    .map((row, i) => ({ row, rank: i + 1 }));
}

/** How many units landed in each quadrant. The landscape screen's four tiles. */
export function quadrantCounts(rows: readonly PriorityRow[]): Record<QuadrantName, number> {
  const counts: Record<QuadrantName, number> = {
    'Quick win': 0,
    'Strategic move': 0,
    'Harmonize first': 0,
    'Defer or retain': 0,
  };
  for (const row of rows) counts[row.quadrant]++;
  return counts;
}

export interface QuadrantOverrideCheck {
  readonly calculated: QuadrantName;
  readonly override: QuadrantName | null;
  readonly differs: boolean;
  readonly reasonRequired: boolean;
  readonly satisfied: boolean;
}

/**
 * §6.5's last sentence: *"Manual override requires a reason."*
 *
 * Same shape and same reasoning as §6.3's `checkProposedComplexity`: the database holds the
 * override and the reason but cannot see the calculated side, so the check is here, and being
 * here is what makes the screen and the route refuse on identical grounds.
 *
 * An override EQUAL to the calculated quadrant needs no reason. It is not an override of
 * anything — and demanding a justification for agreeing with the engine is the kind of friction
 * that teaches people to type "n/a".
 */
export function checkQuadrantOverride(args: {
  readonly calculated: QuadrantName;
  readonly override: QuadrantName | null;
  readonly reason: string | null;
}): QuadrantOverrideCheck {
  const differs = args.override !== null && args.override !== args.calculated;
  const hasReason = args.reason !== null && args.reason.trim() !== '';
  return {
    calculated: args.calculated,
    override: args.override,
    differs,
    reasonRequired: differs,
    satisfied: !differs || hasReason,
  };
}
